import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { AgentAttachment } from '@/services/agent/service'
import { libraryCompaniesService, libraryPeopleService, libraryProductsService } from '@/services/campaigns'
import { heftProfileCurationService, type HeftProfileCuration } from '@/services/heft-profile-curation/heft-profile-curation-service'
import { heftProcessingLensSettingsService } from '@/services/heft-profile-curation/heft-processing-lens-settings-service'
import { NodeAxios } from '@/services/api'
import { formatLensLabel, getDefaultLockedLens, viewsForLens } from '@/modules/campaigns/components/heft-profile/heft-processing-lenses'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onAdd: (attachments: AgentAttachment[]) => void
}

type SubjectType = 'person' | 'company' | 'product'
type SubjectRow = { _id: string; name: string }

export default function HeftProfilePickerDialog({ open, onOpenChange, onAdd }: Props) {
    const [tab, setTab] = useState<SubjectType>('person')
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [items, setItems] = useState<SubjectRow[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [selected, setSelected] = useState<{ subjectType: SubjectType; subjectId: string; name: string } | null>(null)

    const [curation, setCuration] = useState<HeftProfileCuration | null>(null)
    const [effectiveLenses, setEffectiveLenses] = useState<string[]>([])
    const [lens, setLens] = useState<string | null>(null)
    const [viewId, setViewId] = useState<string | null>(null)
    const [preview, setPreview] = useState<any | null>(null)
    const [loadingConfig, setLoadingConfig] = useState(false)
    const [loadingPreview, setLoadingPreview] = useState(false)

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 250)
        return () => clearTimeout(t)
    }, [query])

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setIsLoading(true)

        const search = debouncedQuery.trim() || undefined
        const limit = search ? 30 : 15

        const promise =
            tab === 'person'
                ? libraryPeopleService.listPeople({ search, limit })
                : tab === 'company'
                  ? libraryCompaniesService.listCompanies({ search, limit })
                  : libraryProductsService.listProducts({ search, limit })

        promise
            .then((res: any) => {
                if (cancelled) return
                const payload = res?.data !== undefined ? res.data : res
                const list =
                    tab === 'person'
                        ? payload?.people
                        : tab === 'company'
                          ? payload?.companies
                          : payload?.products
                const rows: SubjectRow[] = Array.isArray(list)
                    ? list.map((x: any) => ({ _id: String(x._id || x.id), name: String(x.name || '') })).filter(r => r._id && r.name)
                    : []
                setItems(rows)
            })
            .catch(() => {
                if (cancelled) return
                setItems([])
            })
            .finally(() => {
                if (cancelled) return
                setIsLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [open, tab, debouncedQuery])

    useEffect(() => {
        if (!open) return
        if (!selected) {
            setCuration(null)
            setEffectiveLenses([])
            setLens(null)
            setViewId(null)
            setPreview(null)
            setLoadingConfig(false)
            setLoadingPreview(false)
            return
        }
        let cancelled = false
        setLoadingConfig(true)
        Promise.all([
            heftProfileCurationService.getOrCreateCuration({ subjectType: selected.subjectType, subjectId: selected.subjectId }),
            heftProcessingLensSettingsService.get({ subjectType: selected.subjectType, subjectId: selected.subjectId }),
        ])
            .then(([curRes, lensRes]) => {
                if (cancelled) return
                const doc = (curRes as any)?.data?.data ?? (curRes as any)?.data ?? (curRes as any)
                const effective = (lensRes as any)?.data?.data?.effective_enabled_lenses ?? (lensRes as any)?.data?.effective_enabled_lenses ?? []
                setCuration(doc as HeftProfileCuration)
                setEffectiveLenses(Array.isArray(effective) ? effective : [])

                const locked = getDefaultLockedLens(selected.subjectType)
                const nextLens = (Array.isArray(effective) && effective.includes(locked) ? locked : effective?.[0]) || locked
                setLens(nextLens)

                const views = Array.isArray((doc as any)?.views) ? (doc as any).views : []
                const primaryId = (doc as any)?.primary_view_id ? String((doc as any).primary_view_id) : null
                const primary = primaryId ? views.find((v: any) => String(v?._id) === primaryId) : null
                const forLens = views.filter((v: any) => String(v?.lens) === nextLens)
                const nextViewId =
                    (primary && String(primary?.lens) === nextLens ? String(primary?._id) : null) ||
                    (forLens.length === 1 ? String(forLens[0]._id) : null) ||
                    (forLens[0]?._id ? String(forLens[0]._id) : null) ||
                    (primary?._id ? String(primary._id) : null) ||
                    (views[0]?._id ? String(views[0]._id) : null)
                setViewId(nextViewId)
            })
            .catch(() => {
                if (cancelled) return
                setCuration(null)
                setEffectiveLenses([])
                setLens(null)
                setViewId(null)
            })
            .finally(() => {
                if (cancelled) return
                setLoadingConfig(false)
            })

        return () => {
            cancelled = true
        }
    }, [open, selected])

    useEffect(() => {
        if (!open) return
        if (!selected || !lens) return
        let cancelled = false
        setLoadingPreview(true)
        const params: any = { lens }
        if (viewId) params.viewId = viewId
        NodeAxios.get(`/heft-profile/${selected.subjectType}/${selected.subjectId}/resolved-context`, { params })
            .then((res: any) => {
                if (cancelled) return
                const data = res?.data?.data ?? res?.data ?? res
                setPreview(data)
            })
            .catch(() => {
                if (cancelled) return
                setPreview(null)
            })
            .finally(() => {
                if (cancelled) return
                setLoadingPreview(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, selected, lens, viewId])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return items
        return items.filter(i => i.name.toLowerCase().includes(q))
    }, [items, query])

    const chooseSubject = (row: SubjectRow) => {
        setSelected({ subjectType: tab, subjectId: row._id, name: row.name })
    }

    const attach = () => {
        if (!selected) return
        const views = curation?.views ?? []
        const hasMultipleInLens = lens ? viewsForLens(views as any, lens).length > 1 : false
        const chosenViewId = viewId && (!hasMultipleInLens || viewId) ? viewId : null
        onAdd([
            {
                kind: 'heft_profile',
                id: selected.subjectId,
                subjectType: selected.subjectType,
                name: selected.name,
                lens: lens || undefined,
                viewId: chosenViewId || undefined,
                lensLabel: preview?.lensLabel ?? (lens ? formatLensLabel(lens, selected.subjectType) : undefined),
                viewName: preview?.viewName ?? undefined,
            },
        ])
        onOpenChange(false)
        setQuery('')
        setSelected(null)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='w-[calc(100vw-24px)] max-w-xl max-h-[calc(100dvh-24px)] flex flex-col gap-3'>
                <DialogHeader>
                    <DialogTitle>{selected ? 'Choose bio slice + Persona' : 'Attach a HEFT profile'}</DialogTitle>
                </DialogHeader>

                {!selected ? (
                    <Tabs value={tab} onValueChange={v => setTab(v as SubjectType)}>
                        <TabsList>
                            <TabsTrigger value='person'>People</TabsTrigger>
                            <TabsTrigger value='company'>Companies</TabsTrigger>
                            <TabsTrigger value='product'>Products</TabsTrigger>
                        </TabsList>

                        <TabsContent value={tab} className='mt-4 space-y-3'>
                            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder='Search…' />

                            <div className='max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white'>
                                {isLoading ? (
                                    <div className='p-3 text-sm text-slate-500'>Loading…</div>
                                ) : filtered.length === 0 ? (
                                    <div className='p-3 text-sm text-slate-500'>
                                        {debouncedQuery.trim() ? 'No matches.' : 'No recent profiles found.'}
                                    </div>
                                ) : (
                                    <div className='divide-y divide-slate-100'>
                                        {filtered.slice(0, 50).map(row => (
                                            <button
                                                key={row._id}
                                                type='button'
                                                onClick={() => chooseSubject(row)}
                                                className='w-full min-h-[44px] px-3 py-2 text-left hover:bg-slate-50'
                                            >
                                                <div className='flex items-center gap-3'>
                                                    <div className='flex-1 min-w-0'>
                                                        <div className='truncate text-sm font-medium text-slate-900'>{row.name}</div>
                                                        <div className='text-xs text-slate-500'>{tab}</div>
                                                    </div>
                                                    <span className='shrink-0 whitespace-nowrap text-xs font-semibold text-primary-main'>Next</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                ) : (
                    <div className='space-y-4'>
                        <div className='rounded-lg border border-slate-200 bg-slate-50 p-3'>
                            <div className='text-sm font-semibold text-slate-900'>{selected.name}</div>
                            <div className='text-xs text-slate-500'>{selected.subjectType}</div>
                        </div>

                        {loadingConfig ? (
                            <div className='text-sm text-slate-500'>Loading personas…</div>
                        ) : (
                            <>
                                <div className='space-y-2'>
                                    <div className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Bio slice</div>
                                    <div className='flex flex-wrap gap-2'>
                                        {(effectiveLenses.length ? effectiveLenses : [getDefaultLockedLens(selected.subjectType)]).map((k) => (
                                            <button
                                                key={k}
                                                type='button'
                                                onClick={() => {
                                                    setLens(k)
                                                    const views = curation?.views ?? []
                                                    const forLens = viewsForLens(views as any, k)
                                                    const next = forLens.length === 1 ? forLens[0]._id : forLens[0]?._id ?? null
                                                    setViewId(next ? String(next) : null)
                                                }}
                                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                                    lens === k ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                }`}
                                            >
                                                {formatLensLabel(k, selected.subjectType)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className='space-y-2'>
                                    <div className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Persona</div>
                                    <select
                                        value={viewId ?? ''}
                                        onChange={(e) => setViewId(e.target.value || null)}
                                        className='w-full h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800'
                                    >
                                        <option value=''>Select a persona…</option>
                                        {(lens ? viewsForLens(curation?.views ?? [], lens) : curation?.views ?? []).map((v: any) => (
                                            <option key={v._id} value={String(v._id)}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </select>
                                    {lens && viewsForLens(curation?.views ?? [], lens).length > 1 && !viewId ? (
                                        <div className='text-xs text-amber-700'>Choose a persona for this bio slice.</div>
                                    ) : null}
                                </div>

                                <div className='rounded-lg border border-slate-200 bg-white p-3'>
                                    <div className='text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2'>Preview</div>
                                    {loadingPreview ? (
                                        <div className='text-sm text-slate-500'>Loading preview…</div>
                                    ) : preview ? (
                                        <div className='space-y-2'>
                                            <div className='text-sm text-slate-800'>
                                                Using: <span className='font-semibold'>{preview.lensLabel}</span>
                                                {preview.viewName ? (
                                                    <>
                                                        {' '}· <span className='font-semibold'>{preview.viewName}</span>
                                                    </>
                                                ) : null}
                                            </div>
                                            <div className='text-xs text-slate-500'>
                                                {Array.isArray(preview.claims) ? `${preview.claims.length} claims` : 'No claims'}
                                                {preview.source ? ` · ${preview.source}` : ''}
                                            </div>
                                            {preview.positioning_statement ? (
                                                <div className='text-sm text-slate-700 line-clamp-3'>{preview.positioning_statement}</div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className='text-sm text-slate-500'>No preview available.</div>
                                    )}
                                </div>

                                <div className='flex items-center justify-between gap-2'>
                                    <button
                                        type='button'
                                        className='min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50'
                                        onClick={() => setSelected(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type='button'
                                        className='min-h-[44px] rounded-lg bg-primary-main px-3 py-2 text-sm font-semibold text-white disabled:opacity-60'
                                        disabled={Boolean(lens && viewsForLens(curation?.views ?? [], lens).length > 1 && !viewId)}
                                        onClick={attach}
                                    >
                                        Attach
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

